import React, { useState, useEffect, useContext } from 'react';
import { Form, Input, Select, DatePicker, Row, Col, Checkbox, message, Upload, Tooltip } from 'antd';
import ValidatedInputNumber from '../../Common/ValidatedInputNumber/ValidatedInputNumber';
import { Modal, Button } from '../../../design-system';
import { BUTTON_PRESETS } from '../../../design-system/constants/buttonPresets';
import { ArrowLeftOutlined, InfoCircleOutlined, UploadOutlined } from '@ant-design/icons';
import nodeAPI from '../../../service/ApiServiceNode';
import dayjs from 'dayjs';
import './client-project-form.scss';
import { Context } from '../../../context/store';
import { MONEY_AMOUNTS } from '../../../constants/financialPrecision';
import { smartDecimalFormatter, smartDecimalParser } from '../../../utils/numberFormatUtils';
import logger from '../../../utils/logger';



const { Option } = Select;
const { TextArea } = Input;

const InternalProjectForm = ({ open, onClose, onSuccess, onBack }) => {
    const [form] = Form.useForm();
    const [globalState] = useContext(Context);
    const [dataLoading, setDataLoading] = useState(false);
    
    const [loading, setLoading] = useState(false);
    
    // State for form controls
    const [noVendorCost, setNoVendorCost] = useState(false);
    const [useUrl, setUseUrl] = useState(false);
    
    // API data states
    const [currencies, setCurrencies] = useState([]);
    const [projectTypes, setProjectTypes] = useState([]);
    const [checklists, setChecklists] = useState([]);
    const [users, setUsers] = useState([]);
    
    // Auto-filled fields tracking
    const [autoFilledFields, setAutoFilledFields] = useState(new Set());

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        if (open) {
            fetchFormData();
            // Set default values for internal project
            const today = dayjs();
            logger.debug('Setting today date:', today.format('YYYY-MM-DD'), today.toDate());
            form.setFieldsValue({
                status: 'In-Planning',
                startDate: today, // Default to today
                personInCharge: globalState?.user?.id // Default to current user
                // Currency will be set to USD dynamically after loading from database
            });
            
            // Mark auto-filled fields
            setAutoFilledFields(new Set(['startDate']));
        }
    }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

    const fetchFormData = async () => {
        setDataLoading(true);
        try {
            // Fetch all necessary data in parallel (using correct endpoints)
            const [
                currenciesRes,
                usersRes,
                projectTypesRes,
                checklistTemplatesRes,
                checklistsRes
            ] = await Promise.all([
                nodeAPI.get('/project/currency'), // New project-specific currency endpoint
                nodeAPI.get('/project/users'),
                nodeAPI.get('/project/classifications?view=dropdown'),
                nodeAPI.get('/checklist/template'),
                nodeAPI.get('/checklist')
            ]);

            // Process currencies from database (same logic as backend)
            const currencyData = currenciesRes.data?.data?.currencies || 
                               currenciesRes.data?.currencies || 
                               [];
            
            if (Array.isArray(currencyData)) {
                setCurrencies(currencyData);
                logger.debug('Loaded currencies from database:', currencyData.length);
                
                // Set USD as default currency if available
                const usdCurrency = currencyData.find(c => c.iso === 'USD');
                if (usdCurrency && !form.getFieldValue('currency')) {
                    form.setFieldsValue({ currency: usdCurrency.id });
                }
            } else {
                logger.warn('Currency data is not an array:', currencyData);
                setCurrencies([]);
            }

            // Process users (same logic as ClientProjectForm)
            const responseData = usersRes.data?.data || usersRes.data || {};
            const fetchedUsers = responseData.users || [];
            
            if (Array.isArray(fetchedUsers)) {
                setUsers(fetchedUsers);
                logger.debug('Users fetched from project endpoint:', fetchedUsers.length);
            } else {
                logger.warn('Users response is not an array:', fetchedUsers);
                setUsers([]);
            }

            // Process project classifications from database (already sorted alphabetically)
            const classResponseData = projectTypesRes.data?.data || projectTypesRes.data || {};
            const classData = classResponseData.data || classResponseData.classifications || [];
            
            if (Array.isArray(classData)) {
                setProjectTypes(classData);
                logger.debug('Loaded project classifications from database:', classData.length);
            } else {
                logger.warn('Project classifications data is not an array:', classData);
                setProjectTypes([]);
            }

            // Process checklists (same logic as ClientProjectForm)
            const templatesResponseData = checklistTemplatesRes.data?.data || checklistTemplatesRes.data || {};
            const templates = templatesResponseData.templates || [];
            
            const checklistsResponseData = checklistsRes.data?.data || checklistsRes.data || {};
            const existingChecklists = checklistsResponseData.checklists || [];
            
            // Combine templates and existing checklists
            const formattedTemplates = Array.isArray(templates)
                ? templates.map(template => ({
                    ...template,
                    isTemplate: true,
                    department_name: template.department_name || 'All Department'
                }))
                : [];
            
            const formattedExisting = Array.isArray(existingChecklists)
                ? existingChecklists
                    .filter(checklist => !checklist.is_template) // Only non-template checklists
                    .map(checklist => ({
                        ...checklist,
                        isTemplate: false
                    }))
                : [];
            
            const allChecklists = [...formattedTemplates, ...formattedExisting];
            setChecklists(allChecklists);
            logger.debug('Loaded checklists:', {
                templates: formattedTemplates.length,
                existing: formattedExisting.length,
                total: allChecklists.length
            });


        } catch (error) {
            logger.error('Error fetching form data:', error);
            message.error('Some data could not be loaded. Please refresh the page.');
        } finally {
            setDataLoading(false);
        }
    };

    // Date validation helper function
    const getDisabledDate = (currentField, values) => {
        return (currentDate) => {
            if (!currentDate) return false;
            
            const formValues = form.getFieldsValue();
            
            // Date hierarchy: startDate <= endDate
            switch (currentField) {
                case 'startDate':
                    // Start date must be <= end date
                    const endDate = formValues.endDate;
                    if (endDate) {
                        return currentDate.isAfter(endDate, 'day');
                    }
                    return false;
                    
                case 'endDate':
                    // End date must be >= start date
                    const startDate = formValues.startDate;
                    if (startDate) {
                        return currentDate.isBefore(startDate, 'day');
                    }
                    return false;
                    
                default:
                    return false;
            }
        };
    };

    const onFinish = async (values) => {
        setLoading(true);
        
        try {
            // Prepare the project data
            const projectData = {
                ...values,
                type: 'INTERNAL',
                client: 1, // Always Mediatropy for internal projects
                startDate: values.startDate ? values.startDate.format('YYYY-MM-DD') : null,
                endDate: values.endDate ? values.endDate.format('YYYY-MM-DD') : null,
                noEstimatedVendorCost: noVendorCost,
                estimatedVendorCost: !noVendorCost ? values.estimatedVendorCost : 0,
                // No lead-related fields for internal projects
                lead: null,
                probability: null,
                leadSource: null,
                winDate: null,
                proposalDate: null,
                proposalFollowUpDate: null,
                presentationDate: null,
                retainer: 'No',
                nonBillable: 'No'
            };

            // File upload is handled by Ant Design Upload component
            // The onChange handler already sets planFilepath and planFilename
            projectData.plan_filepath = values.planFilepath || null;
            projectData.plan_filename = values.planFilename || null;

            // Create the project
            const response = await nodeAPI.post('/project', projectData);
            
            if (response.data?.statusCode?.code === 1) {
                form.resetFields();
                onClose(); // Close modal
                message.success('Internal project created successfully!');
                
                // Call success callback if provided
                if (onSuccess) {
                    onSuccess(response.data);
                }
                
                // Dispatch event to trigger tab switch to internal tab with slight delay
                setTimeout(() => {
                    logger.info('Dispatching project-created event for internal project');
                    const event = new CustomEvent('project-created', {
                        detail: {
                            targetTab: 'internal',
                            projectType: 'INTERNAL',
                            projectData: response.data?.data?.project,
                            refresh: true
                        }
                    });
                    logger.debug('Event detail:', event.detail);
                    window.dispatchEvent(event);
                }, 100);
            } else {
                message.error(response.data?.statusCode?.msg || response.data?.message || 'Failed to create project');
            }
        } catch (error) {
            logger.error('Error creating internal project:', error);
            message.error('Failed to create project');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal
            visible={open}
            onClose={onClose}
            width="xl"
            className="client-project-form-modal"
        >
            <Modal.Header
                title="ADD INTERNAL PROJECT"
                actions={
                    <Button
                        variant="secondary"
                        size="small"
                        icon={<ArrowLeftOutlined />}
                        onClick={onBack}
                    >
                        Back to Selection
                    </Button>
                }
            />

            <Modal.Body padding="none">
                {/* Legend for auto-filled fields */}
                <div style={{ marginBottom: 20, marginTop: 16, display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                    <div style={{ fontSize: 12, color: '#666' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <span style={{
                                width: 16,
                                height: 16,
                                backgroundColor: '#d4edda',
                                border: '1px solid #c3e6cb',
                                display: 'inline-block',
                                borderRadius: '2px'
                            }}></span>
                            <span>Auto-filled field</span>
                        </div>
                    </div>
                </div>
            
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={onFinish}
                    onFinishFailed={(errorInfo) => {
                        logger.debug('Form validation failed:', errorInfo);
                        message.error('Please check all required fields');
                    }}
                    scrollToFirstError={{
                        behavior: 'smooth',
                        block: 'center'
                    }}
                    className="client-project-form"
                >
                {/* Row 1: Project Name (col-4), Project Types (flexible), Client (flexible disabled) */}
                <Row gutter={24}>
                    <Col span={6}>
                        <Form.Item
                            label="Project name"
                            name="name"
                            rules={[{ required: true, message: 'Please enter project name' }]}
                        >
                            <Input placeholder="Enter project name" maxLength={255} />
                        </Form.Item>
                    </Col>
                    <Col span={9}>
                        <Form.Item
                            label="Project classifications"
                            name="projectTypes"
                            rules={[{ required: true, message: 'Please select project classification' }]}
                        >
                            <Select
                                mode="multiple"
                                placeholder="Select project types"
                                loading={dataLoading}
                                showSearch
                                optionFilterProp="children"
                                filterOption={(input, option) =>
                                    option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                }
                            >
                                {Array.isArray(projectTypes) && projectTypes.map(type => (
                                    <Option key={type.id} value={type.id}>
                                        {type.name || 'Unnamed Type'}
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col span={9}>
                        <Form.Item
                            label={
                                <span style={{ color: '#999' }}>
                                    Client <span style={{ fontSize: '11px' }}>(Fixed for internal projects)</span>
                                </span>
                            }
                            name="client"
                        >
                            <Select disabled defaultValue={1}>
                                <Option value={1}>Mediatropy</Option>
                            </Select>
                        </Form.Item>
                    </Col>
                </Row>

                {/* Row 2: Person in Charge (col-4), Start Date, End Date, Status */}
                <Row gutter={24}>
                    <Col span={6}>
                        <Form.Item
                            label="Person in charge"
                            name="personInCharge"
                            rules={[{ required: true, message: 'Please select PIC' }]}
                        >
                            <Select
                                placeholder="Type/Select one"
                                showSearch
                                loading={dataLoading}
                                optionFilterProp="children"
                                filterOption={(input, option) => {
                                    const optionText = typeof option.props.children === 'string' 
                                        ? option.props.children 
                                        : String(option.props.children || '').replace('*', '');
                                    return optionText.toLowerCase().includes(input.toLowerCase());
                                }}
                            >
                                {(() => {
                                    if (!Array.isArray(users) || users.length === 0) {
                                        return <Option disabled>No users available</Option>;
                                    }
                                    
                                    const activeUsers = users.filter(u => u.status === 1 || u.status === '1');
                                    const inactiveUsers = users.filter(u => u.status !== 1 && u.status !== '1');
                                    
                                    return (
                                        <>
                                            {activeUsers.length > 0 && (
                                                <Select.OptGroup label="Active user">
                                                    {activeUsers.map(user => {
                                                        const displayName = user.full_name || 
                                                            `${user.first_name || ''} ${user.last_name || ''}`.trim() || 
                                                            user.email || 'Unknown User';
                                                        return (
                                                            <Option key={user.id} value={user.id}>
                                                                {displayName}
                                                            </Option>
                                                        );
                                                    })}
                                                </Select.OptGroup>
                                            )}
                                            {inactiveUsers.length > 0 && (
                                                <Select.OptGroup label="Deactivated user">
                                                    {inactiveUsers.map(user => {
                                                        const displayName = user.full_name || 
                                                            `${user.first_name || ''} ${user.last_name || ''}`.trim() || 
                                                            user.email || 'Unknown User';
                                                        return (
                                                            <Option key={user.id} value={user.id} className="inactive-option">
                                                                {displayName}*
                                                            </Option>
                                                        );
                                                    })}
                                                </Select.OptGroup>
                                            )}
                                        </>
                                    );
                                })()}
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col span={6}>
                        <Form.Item
                            label="Project Start Date"
                            name="startDate"
                            className={autoFilledFields.has('startDate') ? 'auto-filled-field' : ''}
                        >
                            <DatePicker 
                                style={{ width: '100%' }}
                                format="DD/MM/YYYY"
                                placeholder="Select Date"
                                showToday={true}
                                disabledDate={getDisabledDate('startDate')}
                            />
                        </Form.Item>
                    </Col>
                    <Col span={6}>
                        <Form.Item
                            label="Project End Date"
                            name="endDate"
                        >
                            <DatePicker 
                                style={{ width: '100%' }}
                                format="DD/MM/YYYY"
                                placeholder="Select Date"
                                showToday={true}
                                disabledDate={getDisabledDate('endDate')}
                            />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            label="Status"
                            name="status"
                            rules={[{ required: true, message: 'Please select status' }]}
                        >
                            <Select placeholder="Select one">
                                <Option value="In-Planning">In-Planning</Option>
                                <Option value="On-Going">On-Going</Option>
                                <Option value="Paused">Paused</Option>
                                <Option value="Canceled">Canceled</Option>
                                <Option value="Finished">Finished</Option>
                            </Select>
                        </Form.Item>
                    </Col>
                </Row>

                {/* Row 3: Currency (col-3), Estimated Vendor Cost (flexible) */}
                <Row gutter={24}>
                    <Col span={6}>
                        <Form.Item
                            label="Currency"
                            name="currency"
                            rules={[{ required: true, message: 'Please select currency' }]}
                        >
                            <Select
                                placeholder="Select one"
                                showSearch
                                optionFilterProp="children"
                                filterOption={(input, option) =>
                                    option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                }
                            >
                                {Array.isArray(currencies) && currencies.map(currency => (
                                    <Option key={currency.id} value={currency.id}>
                                        {currency.iso} ({currency.name})
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col span={18}>
                        <Form.Item
                            label="Estimated vendors costs"
                            name="estimatedVendorCost"
                            rules={[{ 
                                required: !noVendorCost, 
                                message: 'Please enter vendor cost' 
                            }]}
                            style={{ marginBottom: 8 }}
                        >
                            <ValidatedInputNumber
                                style={{ width: '100%' }}
                                placeholder="0.00"
                                formatter={(value) => smartDecimalFormatter(value, MONEY_AMOUNTS)}
                                parser={smartDecimalParser}
                                min={0}
                                step={0.01}
                                precision={MONEY_AMOUNTS.precision}
                                keyboard={false}
                                disabled={noVendorCost}
                            />
                        </Form.Item>
                        <Checkbox 
                            checked={noVendorCost}
                            onChange={(e) => {
                                setNoVendorCost(e.target.checked);
                                if (e.target.checked) {
                                    form.setFieldsValue({ estimatedVendorCost: 0 });
                                }
                            }}
                        >
                            No Vendors Costs
                        </Checkbox>
                    </Col>
                </Row>

                {/* Row 4: GDrive URL (col-12), Project Plan Attachment (col-12) */}
                <Row gutter={24}>
                    <Col span={12}>
                        <Form.Item
                            label="GDrive folder URL"
                            name="planURL"
                        >
                            <Input 
                                placeholder="https://drive.google.com/..."
                                pattern="^(https?:\/\/)?(www\.)?([a-zA-Z0-9]+(-?[a-zA-Z0-9])*\.)+[\w]{2,}(\/\S*)?$"
                            />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        {/* Hidden field to store the actual file path string */}
                        <Form.Item name="planFilepath" hidden>
                            <Input />
                        </Form.Item>
                        {/* Hidden field to store the filename */}
                        <Form.Item name="planFilename" hidden>
                            <Input />
                        </Form.Item>

                        <Form.Item
                            label={
                                <span>
                                    Project plan attachment{' '}
                                    <Tooltip title="Max Size 10MB, Supported File: .Jpg, .Jpeg, .Png, .Gif, .Webp, .Pdf, .Xls, .Xlsx, .Ppt, .Pptx">
                                        <InfoCircleOutlined style={{ color: '#8c8c8c' }} />
                                    </Tooltip>
                                </span>
                            }
                        >
                            {!useUrl ? (
                                <Upload
                                    maxCount={1}
                                    beforeUpload={async (file) => {
                                        // Validate file size
                                        const isLt10M = file.size / 1024 / 1024 < 10;
                                        if (!isLt10M) {
                                            message.error('File must be smaller than 10MB!');
                                            return false;
                                        }

                                        // Validate file type
                                        const allowedTypes = [
                                            'image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp',
                                            'application/pdf',
                                            'application/vnd.ms-excel',
                                            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                                            'application/vnd.ms-powerpoint',
                                            'application/vnd.openxmlformats-officedocument.presentationml.presentation'
                                        ];

                                        if (!allowedTypes.includes(file.type)) {
                                            message.error('Invalid file type. Supported: JPG, PNG, GIF, WebP, PDF, XLS, XLSX, PPT, PPTX');
                                            return false;
                                        }

                                        // Upload using nodeAPI (manual upload pattern)
                                        try {
                                            const formData = new FormData();
                                            formData.append('file', file);

                                            message.loading(`Uploading ${file.name}...`, 0);

                                            const uploadResponse = await nodeAPI.post('/project/upload/plan', formData, {
                                                headers: { 'Content-Type': 'multipart/form-data' }
                                            });

                                            message.destroy(); // Clear loading message

                                            // Handle response format from lib-upload.js
                                            if (uploadResponse.data?.statusCode?.code === 1) {
                                                const responseData = uploadResponse.data.data || uploadResponse.data;
                                                const s3Path = responseData?.path;
                                                const originalFileName = file.name;

                                                if (s3Path) {
                                                    message.success(`${file.name} uploaded successfully`);

                                                    // Set the form values
                                                    form.setFieldsValue({
                                                        planFilepath: s3Path,
                                                        planFilename: originalFileName
                                                    });
                                                } else {
                                                    message.error('Upload succeeded but could not process response');
                                                }
                                            } else {
                                                const errorMsg = uploadResponse.data?.statusCode?.msg ||
                                                               uploadResponse.data?.statusCode?.debug ||
                                                               'Invalid response format';
                                                message.error('Upload failed: ' + errorMsg);
                                            }
                                        } catch (uploadError) {
                                            message.destroy();
                                            logger.error('Upload error:', uploadError);
                                            message.error(`Upload failed: ${file.name}`);
                                        }

                                        return false; // Prevent automatic upload
                                    }}
                                    accept=".jpg,.jpeg,.png,.gif,.webp,.pdf,.xls,.xlsx,.ppt,.pptx"
                                >
                                    <Button
                                        variant="secondary"
                                        icon={<UploadOutlined />}
                                        style={{ width: '100%' }}
                                    >
                                        Choose File
                                    </Button>
                                </Upload>
                            ) : (
                                <Input 
                                    placeholder="Project plan URL"
                                    pattern="^(https?:\/\/)?(www\.)?([a-zA-Z0-9]+(-?[a-zA-Z0-9])*\.)+[\w]{2,}(\/\S*)?$"
                                />
                            )}
                            <div style={{ marginTop: 8 }}>
                                <Checkbox 
                                    checked={useUrl}
                                    onChange={(e) => setUseUrl(e.target.checked)}
                                >
                                    Use URL
                                </Checkbox>
                            </div>
                        </Form.Item>
                    </Col>
                </Row>

                {/* Row 5: Description (col-8), Checklists (col-4) */}
                <Row gutter={24}>
                    <Col span={16}>
                        <Form.Item
                            label="Description"
                            name="description"
                        >
                            <TextArea 
                                placeholder="Enter project description..."
                                rows={6}
                                style={{ 
                                    resize: 'vertical !important', 
                                    minHeight: '150px !important',
                                    maxHeight: '400px !important',
                                    overflow: 'auto !important'
                                }}
                            />
                        </Form.Item>
                    </Col>
                    <Col span={8}>
                        <Form.Item
                            label="Project Checklists"
                            name="checklists"
                        >
                            <Select
                                mode="multiple"
                                placeholder="-- Select checklists --"
                                loading={dataLoading}
                                showSearch
                                optionFilterProp="children"
                                filterOption={(input, option) =>
                                    option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                }
                            >
                                {(() => {
                                    // Group checklists by department for templates, or "Existing" for non-templates
                                    const grouped = {};
                                    
                                    checklists.forEach(checklist => {
                                        const groupName = checklist.isTemplate 
                                            ? (checklist.department_name || 'All Department')
                                            : 'Existing';
                                        
                                        if (!grouped[groupName]) {
                                            grouped[groupName] = [];
                                        }
                                        grouped[groupName].push(checklist);
                                    });

                                    // Sort groups: All Department first, then alphabetically, Existing last
                                    const sortedGroups = Object.keys(grouped).sort((a, b) => {
                                        if (a === 'All Department') return -1;
                                        if (b === 'All Department') return 1;
                                        if (a === 'Existing') return 1;
                                        if (b === 'Existing') return -1;
                                        return a.localeCompare(b);
                                    });

                                    return sortedGroups.map(groupName => (
                                        <Select.OptGroup key={groupName} label={groupName}>
                                            {grouped[groupName].map(checklist => (
                                                <Option key={checklist.id} value={checklist.id}>
                                                    {checklist.name}
                                                </Option>
                                            ))}
                                        </Select.OptGroup>
                                    ));
                                })()}
                            </Select>
                        </Form.Item>
                    </Col>
                </Row>
            </Form>
            </Modal.Body>

            <Modal.Footer
                buttons={[
                    {
                        ...BUTTON_PRESETS.CANCEL,
                        key: 'cancel',
                        label: 'Cancel',
                        onClick: onClose
                    },
                    {
                        ...BUTTON_PRESETS.SAVE,
                        key: 'create',
                        label: 'Create Project',
                        onClick: () => {
                            form.submit();
                        },
                        loading: loading,
                        disabled: loading
                    }
                ]}
            />
        </Modal>
    );
};

export default InternalProjectForm;
